import React, { useEffect, useMemo, useState } from "react";
import Pagination from "../../components/Pagination";
import { scrollToIslandTop } from "../shared/forms/scroll";

function normaliseFilterValue(value) {
  return value && value !== "All" ? value : "";
}

function readOptionLabel(item, key) {
  if (!item || typeof item !== "object") {
    return "";
  }

  return item[key] || "";
}

function buildOptions(items, key) {
  return ["All", ...(Array.isArray(items) ? items.map((item) => readOptionLabel(item, key)).filter(Boolean) : [])];
}

function buildPageUrl(baseUrl, filters) {
  const nextUrl = new URL(baseUrl, window.location.origin);

  nextUrl.searchParams.set("customer", filters.customer);
  nextUrl.searchParams.set("sort", filters.sort);
  nextUrl.searchParams.set("pageNumber", String(filters.pageNumber));
  nextUrl.searchParams.set("searchQuery", filters.searchQuery);
  nextUrl.searchParams.set("status", filters.status);
  nextUrl.searchParams.set("orderByAscending", String(filters.orderByAscending));
  nextUrl.searchParams.set("release", filters.release);

  return `${nextUrl.pathname}${nextUrl.search}`;
}

function buildDownloadUrl(downloadUrl, filters) {
  const nextUrl = new URL(downloadUrl, window.location.origin);

  nextUrl.searchParams.set("status", filters.status);
  nextUrl.searchParams.set("impact", filters.customer);
  nextUrl.searchParams.set("delivery", filters.release);
  nextUrl.searchParams.set("search", filters.searchQuery);

  return `${nextUrl.pathname}${nextUrl.search}`;
}

function FilterDropdown({ id, label, value, options, onChange }) {
  return (
    <div className="col-lg-3 col-md-6 mt-3 align-self-end">
      <div className="label font-weight-semibold">{label}</div>
      <div className="dropdown">
        <button
          className="btn-dropdown dropdown-toggle pr-0"
          type="button"
          id={id}
          data-bs-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          {value}
        </button>
        <div className="dropdown-menu" aria-labelledby={id}>
          {options.map((item) => (
            <button
              key={`${id}-${item}`}
              className={`dropdown-item ${item === value ? "active" : ""}`}
              type="button"
              onClick={() => onChange(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SortToggle({ label, sortKey, activeSort, orderByAscending, onClick, wrapLabel = true }) {
  return (
    <div
      className={`d-flex align-items-start sort-toggle ${orderByAscending && activeSort === sortKey ? "desc" : ""}`}
      onClick={() => onClick(sortKey)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(sortKey);
        }
      }}
    >
      {wrapLabel ? <span className="mr-2">{label}</span> : label}
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="ml-auto flex-shrink-0" viewBox="0 0 16 16">
        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
      </svg>
    </div>
  );
}

export default function ChangeRegisterIsland({ config }) {
  const [status, setStatus] = useState(config.initialStatus || "All");
  const [customer, setCustomer] = useState(config.initialCustomer || "All");
  const [release, setRelease] = useState(config.initialRelease || "All");
  const [searchQuery, setSearchQuery] = useState(config.initialSearchQuery || "");
  const [sort, setSort] = useState(config.initialSort ?? "Date");
  const [orderByAscending, setOrderByAscending] = useState(Boolean(config.initialOrderByAscending));
  const [currentPage, setCurrentPage] = useState(Number(config.initialPageNumber) || 1);
  const [changeData, setChangeData] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(Number(config.pageSize) || 12);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [customerOptions, setCustomerOptions] = useState(["All"]);
  const [releaseOptions, setReleaseOptions] = useState(["All"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const downloadResultsUrl = useMemo(
    () =>
      buildDownloadUrl(config.downloadUrl, {
        status,
        customer,
        release,
        searchQuery,
      }),
    [config.downloadUrl, customer, release, searchQuery, status],
  );

  useEffect(() => {
    let isActive = true;

    async function loadResults() {
      document.documentElement.classList.add("loading");
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();
        params.append("nodeId", config.nodeId);
        params.append("customer", normaliseFilterValue(customer));
        params.append("sort", sort);
        params.append("searchQuery", searchQuery);
        params.append("pageNumber", String(currentPage));
        params.append("status", normaliseFilterValue(status));
        params.append("orderByAscending", String(orderByAscending));
        params.append("release", normaliseFilterValue(release));

        const response = await fetch(config.filterApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
          },
          body: params.toString(),
        });

        if (!response.ok) {
          throw new Error(`Change register request failed with ${response.status}`);
        }

        const result = await response.json();

        if (!isActive) {
          return;
        }

        setChangeData(Array.isArray(result.Results) ? result.Results : []);
        setStatusOptions(buildOptions(result.StatusFilter, "Status"));
        setCustomerOptions(buildOptions(result.CustomerFilter, "Customer"));
        setReleaseOptions(buildOptions(result.ReleaseTypeFilter, "ReleaseType"));
        setTotal(Number(result.TotalNumberOfRecords) || 0);
        setPageSize(Number(result.PageSize) || Number(config.pageSize) || 12);
        setCurrentPage(Number(result.PageNumber) || 1);
        setOrderByAscending(Boolean(result.OrderByAscending));

        const nextUrl = buildPageUrl(config.baseUrl, {
          customer,
          sort,
          pageNumber: Number(result.PageNumber) || 1,
          searchQuery,
          status,
          orderByAscending: Boolean(result.OrderByAscending),
          release,
        });

        window.history.pushState({ path: nextUrl }, document.title, nextUrl);
        scrollToIslandTop(".main", 0);
      } catch (requestError) {
        if (!isActive) {
          return;
        }

        setChangeData([]);
        setTotal(0);
        setError(requestError?.message || config.errorText);
      } finally {
        if (!isActive) {
          return;
        }

        setLoading(false);
        document.documentElement.classList.remove("loading");
      }
    }

    loadResults();

    return () => {
      isActive = false;
      document.documentElement.classList.remove("loading");
    };
  }, [
    config.baseUrl,
    config.errorText,
    config.filterApiUrl,
    config.nodeId,
    config.pageSize,
    currentPage,
    customer,
    orderByAscending,
    reloadKey,
    release,
    searchQuery,
    sort,
    status,
  ]);

  const updateFilter = (setter, value) => {
    setter(value);
    setCurrentPage(1);
  };

  const onSearchSubmit = () => {
    setCurrentPage(1);
    setReloadKey((currentValue) => currentValue + 1);
  };

  const onResetFilter = () => {
    setCustomer("All");
    setSort("");
    setSearchQuery("");
    setStatus("All");
    setOrderByAscending(false);
    setRelease("All");
    setCurrentPage(1);
    setReloadKey((currentValue) => currentValue + 1);
  };

  const onSortToggle = (nextSort) => {
    setSort(nextSort);
    setOrderByAscending((currentValue) => !currentValue);
  };

  return (
    <>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      <div className="bg-lightest-blue p-5 rounded-xl">
        <div className="d-flex flex-wrap">
          <div className="h4 font-weight-semibold">{config.filterHeading}</div>
          <button className="btn btn-link p-0 align-self-start ml-auto" type="button" onClick={onResetFilter}>
            <u>{config.resetFilterLabel}</u>
          </button>
        </div>
        <div className="row mb-lg-3">
          <FilterDropdown
            id="statusDropdown"
            label={config.statusLabel}
            value={status}
            options={statusOptions}
            onChange={(value) => updateFilter(setStatus, value)}
          />
          <FilterDropdown
            id="customerDropdown"
            label={config.customerLabel}
            value={customer}
            options={customerOptions}
            onChange={(value) => updateFilter(setCustomer, value)}
          />
          <FilterDropdown
            id="releaseDropdown"
            label={config.releaseLabel}
            value={release}
            options={releaseOptions}
            onChange={(value) => updateFilter(setRelease, value)}
          />
          <div className="col-lg-3 col-md-6 mt-3 align-self-end">
            <div className="label font-weight-semibold">{config.searchLabel}</div>
            <div className="form-search">
              <input
                type="text"
                name="search"
                id="filterSearch"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    onSearchSubmit();
                  }
                }}
              />
              <button className="form-search-btn" type="button" onClick={onSearchSubmit} aria-label={config.searchButtonLabel}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="resultsTable mt-4">
        <div className="h3">{config.resultsHeading}</div>
        {!loading && changeData.length > 0 ? (
          <>
            <table className="table table-change table-no-border-mobile">
              <thead>
                <tr>
                  <th>
                    <SortToggle
                      label={config.lastUpdatedLabel}
                      sortKey="Date"
                      activeSort={sort}
                      orderByAscending={orderByAscending}
                      onClick={onSortToggle}
                    />
                  </th>
                  <th>
                    <SortToggle
                      label={config.refNumberLabel}
                      sortKey="ReferenceNumber"
                      activeSort={sort}
                      orderByAscending={orderByAscending}
                      onClick={onSortToggle}
                    />
                  </th>
                  <th>
                    <span>{config.changeTitleLabel}</span>
                  </th>
                  <th>
                    <SortToggle
                      label={config.statusLabel}
                      sortKey="Status"
                      activeSort={sort}
                      orderByAscending={orderByAscending}
                      onClick={onSortToggle}
                    />
                  </th>
                  <th>
                    <SortToggle
                      label={config.proposerLabel}
                      sortKey="Proposer"
                      activeSort={sort}
                      orderByAscending={orderByAscending}
                      onClick={onSortToggle}
                      wrapLabel={false}
                    />
                  </th>
                  <th>
                    <SortToggle
                      label={config.deliveryTypeLabel}
                      sortKey="ReleaseType"
                      activeSort={sort}
                      orderByAscending={orderByAscending}
                      onClick={onSortToggle}
                    />
                  </th>
                  <th>
                    <SortToggle
                      label={config.impactingLabel}
                      sortKey="Impacting"
                      activeSort={sort}
                      orderByAscending={orderByAscending}
                      onClick={onSortToggle}
                    />
                  </th>
                  <th>
                    <span>{config.relatingToLabel}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="card-content">
                {changeData.map((item, index) => (
                  <tr key={`${item.ReferenceNumber || item.Title || "change"}-${index}`}>
                    <td data-title={config.lastUpdatedLabel}>{item.Date}</td>
                    <td data-title={config.refNumberLabel}>
                      <a href={item.Url}>{item.ReferenceNumber}</a>
                    </td>
                    <td data-title={config.changeTitleLabel}>{item.Title}</td>
                    <td data-title={config.statusLabel}>{item.Status}</td>
                    <td data-title={config.proposerLabel}>{item.Proposer}</td>
                    <td data-title={config.deliveryTypeLabel}>
                      <span className={item.ReleaseTypeCssClass}>{item.ReleaseType}</span>
                    </td>
                    <td data-title={config.impactingLabel}>
                      <ul className="list-normal mb-0">
                        {(Array.isArray(item.Customer) ? item.Customer : []).map((customerItem, customerIndex) => (
                          <li key={`${item.ReferenceNumber || index}-customer-${customerIndex}`}>{customerItem}</li>
                        ))}
                      </ul>
                    </td>
                    <td data-title={config.relatingToLabel}>
                      {(Array.isArray(item.RelatingTo) ? item.RelatingTo : []).map((relatingItem, relatingIndex) => (
                        <a className="mb-3" key={`${item.ReferenceNumber || index}-relating-${relatingIndex}`} href={relatingItem.Url}>
                          {relatingItem.Title}
                        </a>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > pageSize ? (
              <div className="mt-4 mt-lg-8">
                <Pagination
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={total}
                  onPageChange={setCurrentPage}
                  previousLabel={config.previousPageLabel}
                  nextLabel={config.nextPageLabel}
                />
              </div>
            ) : null}
          </>
        ) : null}

        {!loading && changeData.length === 0 ? (
          <div className="container my-5">
            <h3 className="sorry">{config.emptyStateText}</h3>
          </div>
        ) : null}

        <a className="btn btn-primary" href={downloadResultsUrl} target="_blank" rel="noopener noreferrer">
          {config.downloadLabel}
        </a>
      </div>
    </>
  );
}
